import type React from 'react';
import { createElement } from 'react';
import { renderHook, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider, type UseQueryResult } from '@tanstack/react-query';
import { vi, describe, it, expect, beforeEach } from 'vitest';
import type { SigningDetails } from 'receivable-invoices-js/react/api/queries/fetch-signing-details/fetch-signing-details';
import { QUOTE_STATUSES } from 'receivable-invoices-js/constants/quotes';
import { useFetchSigningDetails } from 'receivable-invoices-js/react/api/queries/fetch-signing-details/fetch-signing-details';
import {
  useFetchClientPortalSettings,
  type FetchClientPortalSettingsResponse,
} from 'receivable-invoices-js/react/api/queries/fetch-client-portal-settings';
import { useRouteQuery } from './use-route-query';

vi.mock('receivable-invoices-js/react/api/queries/fetch-signing-details/fetch-signing-details');
vi.mock('receivable-invoices-js/react/api/queries/fetch-client-portal-settings');

vi.mock('receivable-invoices-js/constants/hosts', () => ({
  apiBaseURL: 'https://api.example.com',
  receivableInvoicesApiNamespace: '/api/v5',
}));

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

function createMockQueryResult<T>(
  data: T,
  overrides: Partial<UseQueryResult<T>> = {}
): UseQueryResult<T> {
  return {
    data,
    isSuccess: true,
    isLoading: false,
    isError: false,
    error: null,
    refetch: vi.fn(),
    ...overrides,
  } as UseQueryResult<T>;
}

describe(useRouteQuery, () => {
  let queryClient: QueryClient;
  const mockUseFetchSigningDetails = vi.mocked(useFetchSigningDetails);
  const mockUseFetchClientPortalSettings = vi.mocked(useFetchClientPortalSettings);

  const setupMocks = (
    signingData: SigningDetails,
    queryOverrides: Partial<UseQueryResult<SigningDetails>> = {}
  ) => {
    mockUseFetchSigningDetails.mockReturnValue(createMockQueryResult(signingData, queryOverrides));

    mockUseFetchClientPortalSettings.mockReturnValue(
      createMockQueryResult({
        id: 'client-456',
        clientPortalEnabled: true,
      } as FetchClientPortalSettingsResponse)
    );
  };

  beforeEach(() => {
    queryClient = new QueryClient({
      defaultOptions: { queries: { retry: false } },
    });
    mockUseFetchSigningDetails.mockClear();
    mockUseFetchClientPortalSettings.mockClear();
  });

  const wrapper = ({ children }: { children: React.ReactNode }) =>
    createElement(QueryClientProvider, { client: queryClient }, children);

  it('returns error state when signature link is empty', async () => {
    setupMocks({ electronicSignatureUrl: '', status: 'pending_approval' });

    const { result } = renderHook(() => useRouteQuery(), { wrapper });

    await waitFor(() => {
      expect(result.current.isError).toBe(true);
      expect(result.current.signatureLink).toBe('');
    });
  });

  it('returns not-available state when quote is canceled', async () => {
    setupMocks({
      electronicSignatureUrl: 'https://example.com/sign',
      status: QUOTE_STATUSES.CANCELED,
    });

    const { result } = renderHook(() => useRouteQuery(), { wrapper });

    await waitFor(() => {
      expect(result.current.isNotAvailable).toBe(true);
      expect(result.current.signatureLink).toBe('https://example.com/sign');
    });
  });

  it('returns error state when API returns error', async () => {
    setupMocks({
      electronicSignatureUrl: 'https://example.com/sign',
      status: 'pending_approval',
      error: 'Something went wrong',
    });

    const { result } = renderHook(() => useRouteQuery(), { wrapper });

    await waitFor(() => {
      expect(result.current.isError).toBe(true);
      expect(result.current.error).toBe('Something went wrong');
    });
  });

  it('returns valid state when signature link is valid', async () => {
    setupMocks({ electronicSignatureUrl: 'https://example.com/sign', status: 'pending_approval' });

    const { result } = renderHook(() => useRouteQuery(), { wrapper });

    await waitFor(() => {
      expect(result.current.isError).toBe(false);
      expect(result.current.isNotAvailable).toBe(false);
      expect(result.current.signatureLink).toBe('https://example.com/sign');
      expect(result.current.status).toBe('pending_approval');
    });
  });
});
